import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import RichTextEditor from '../../components/admin/RichTextEditor';
import { 
  Loader2,
  Trophy,
  Plus,
  Trash2,
  Edit,
  Eye,
  EyeOff,
  Calendar,
  Link as LinkIcon,
  Image,
  Save,
  X,
  Clock,
  History,
  Info,
  ExternalLink,
  Users,
  UserCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('ca-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

// Check if date is in the past
const isPastDate = (dateStr) => {
  if (!dateStr) return false;
  const today = new Date().toISOString().split('T')[0];
  return dateStr < today;
};

export const AdminTournaments = () => {
  const { isAdmin, authFetch } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tournaments, setTournaments] = useState({ future: [], past: [] });
  const [showEditor, setShowEditor] = useState(false);
  const [editingTournament, setEditingTournament] = useState(null);
  const [activeTab, setActiveTab] = useState('future');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    image: '',
    description: '',
    link_members: '',
    link_visitors: '',
    is_published: false
  });

  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_URL}/api/admin/tournaments`, {  });
      if (response.ok) {
        const data = await response.json();
        setTournaments({ future: data.future || [], past: data.past || [] });
      }
    } catch (error) {
      console.error('Failed to fetch tournaments:', error);
      toast.error('Error carregant torneigs');
    } finally {
      setLoading(false);
    }
  };

  const openEditor = (tournament = null) => {
    if (tournament) {
      setEditingTournament(tournament);
      setFormData({
        name: tournament.name || '',
        date: tournament.date || '',
        image: tournament.image || '',
        description: tournament.description || '',
        link_members: tournament.link_members || '',
        link_visitors: tournament.link_visitors || '',
        is_published: tournament.is_published || false
      });
    } else {
      setEditingTournament(null);
      setFormData({
        name: '',
        date: '',
        image: '',
        description: '',
        link_members: '',
        link_visitors: '',
        is_published: false
      });
    }
    setShowEditor(true);
  };

  const closeEditor = () => {
    setShowEditor(false);
    setEditingTournament(null);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.date) {
      toast.error('Nom i data són obligatoris');
      return;
    }

    if (formData.description.length > 1000) {
      toast.error('La descripció no pot superar els 1000 caràcters');
      return;
    }

    setSaving(true);
    try {
      const url = editingTournament 
        ? `${API_URL}/api/admin/tournaments/${editingTournament.id}`
        : `${API_URL}/api/admin/tournaments`;
      
      const method = editingTournament ? 'PUT' : 'POST';
      
      const response = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingTournament ? 'Torneig actualitzat' : 'Torneig creat');
        closeEditor();
        fetchTournaments();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error guardant torneig');
      }
    } catch (error) {
      toast.error('Error guardant torneig');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async (tournament) => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/tournaments/${tournament.id}/toggle-publish`, {
        method: 'PUT',
        
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.is_published ? 'Torneig publicat' : 'Torneig despublicat');
        fetchTournaments();
      } else {
        toast.error('Error canviant estat de publicació');
      }
    } catch (error) {
      toast.error('Error canviant estat de publicació');
    }
  };

  const handleDelete = async (tournament) => {
    if (!window.confirm(`Segur que vols eliminar "${tournament.name}"?`)) return;
    
    try {
      const response = await authFetch(`${API_URL}/api/admin/tournaments/${tournament.id}`, {
        method: 'DELETE',
        
      });

      if (response.ok) {
        toast.success('Torneig eliminat');
        fetchTournaments();
      } else {
        toast.error('Error eliminant torneig');
      }
    } catch (error) {
      toast.error('Error eliminant torneig');
    }
  };

  const TournamentCard = ({ tournament }) => {
    const isPast = isPastDate(tournament.date);
    
    return (
      <div 
        className={`bg-white border rounded-lg overflow-hidden ${
          tournament.is_published ? 'border-[#E5E5E5]' : 'border-dashed border-[#CCCCCC]'
        }`}
        data-testid={`tournament-${tournament.id}`}
      >
        {/* Image */}
        {tournament.image && (
          <div className="h-40 overflow-hidden bg-gray-100">
            <img 
              src={tournament.image} 
              alt={tournament.name}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        
        {/* Content */}
        <div className="p-4 space-y-3">
          {/* Header with status badges */}
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-[#333333] line-clamp-2">{tournament.name}</h3>
            <div className="flex gap-1 flex-shrink-0">
              {isPast && (
                <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                  Passat
                </span>
              )}
              {tournament.is_published ? (
                <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded">
                  Publicat
                </span>
              ) : (
                <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-700 rounded">
                  Esborrany
                </span>
              )}
            </div>
          </div>
          
          {/* Date */}
          <div className="flex items-center gap-2 text-sm text-[#666666]">
            <Calendar className="w-4 h-4" />
            <span>{formatDate(tournament.date)}</span>
          </div>
          
          {/* Description preview */}
          {tournament.description && (
            <p className="text-sm text-[#666666] line-clamp-2">{tournament.description}</p>
          )}
          
          {/* Links */}
          <div className="flex gap-2 text-xs">
            {tournament.link_members && (
              <a 
                href={tournament.link_members}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-[#1A4D2E] hover:underline"
              >
                <UserCheck className="w-3 h-3" /> Membres
              </a>
            )}
            {tournament.link_visitors && (
              <a 
                href={tournament.link_visitors}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-[#1A4D2E] hover:underline"
              >
                <Users className="w-3 h-3" /> Visitants
              </a>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-[#E5E5E5]">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleTogglePublish(tournament)}
              className={tournament.is_published ? 'text-yellow-600' : 'text-green-600'}
            >
              {tournament.is_published ? (
                <><EyeOff className="w-4 h-4 mr-1" /> Despublicar</>
              ) : (
                <><Eye className="w-4 h-4 mr-1" /> Publicar</>
              )}
            </Button>
            
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openEditor(tournament)}
              >
                <Edit className="w-4 h-4" />
              </Button>
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(tournament)}
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-tournaments">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-semibold text-[#1A4D2E] flex items-center gap-2">
              <Trophy className="w-6 h-6" />
              Gestió de Torneigs
            </h1>
            <p className="text-[#666666] mt-1">
              {tournaments.future.length} futurs · {tournaments.past.length} passats
            </p>
          </div>
          
          <Button
            onClick={() => openEditor()}
            className="bg-[#1A4D2E] hover:bg-[#153d25] text-white gap-2"
            data-testid="add-tournament-btn"
          >
            <Plus className="w-4 h-4" />
            Nou Torneig
          </Button>
        </div>

        {/* Info banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">Com funciona:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li><strong>Passat/Futur:</strong> Es determina automàticament segons la data del torneig</li>
                <li><strong>Publicar:</strong> Els torneigs només es mostren al web públic si estan publicats</li>
                <li><strong>Imatge:</strong> Mida recomanada 800×400 px, format JPG/WebP, màx. 200KB</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-[#E5E5E5]">
          <button
            onClick={() => setActiveTab('future')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'future' 
                ? 'border-[#1A4D2E] text-[#1A4D2E]' 
                : 'border-transparent text-[#666666] hover:text-[#333333]'
            }`}
          >
            <Clock className="w-4 h-4" />
            Futurs ({tournaments.future.length})
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'past' 
                ? 'border-[#1A4D2E] text-[#1A4D2E]' 
                : 'border-transparent text-[#666666] hover:text-[#333333]'
            }`}
          >
            <History className="w-4 h-4" />
            Passats ({tournaments.past.length})
          </button>
        </div>

        {/* Tournament Grid */}
        {activeTab === 'future' && (
          tournaments.future.length === 0 ? (
            <div className="bg-[#F9F9F7] border border-dashed border-[#E5E5E5] rounded-lg p-12 text-center">
              <Trophy className="w-12 h-12 text-[#CCCCCC] mx-auto mb-4" />
              <p className="text-[#666666] mb-4">No hi ha torneigs futurs programats</p>
              <Button onClick={() => openEditor()} variant="outline" className="gap-2">
                <Plus className="w-4 h-4" />
                Crear primer torneig
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tournaments.future.map(t => (
                <TournamentCard key={t.id} tournament={t} />
              ))}
            </div>
          )
        )}

        {activeTab === 'past' && (
          tournaments.past.length === 0 ? (
            <div className="bg-[#F9F9F7] border border-dashed border-[#E5E5E5] rounded-lg p-12 text-center">
              <History className="w-12 h-12 text-[#CCCCCC] mx-auto mb-4" />
              <p className="text-[#666666]">No hi ha torneigs passats</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tournaments.past.map(t => (
                <TournamentCard key={t.id} tournament={t} />
              ))}
            </div>
          )
        )}

        {/* Editor Dialog */}
        <Dialog open={showEditor} onOpenChange={setShowEditor}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5" />
                {editingTournament ? 'Editar Torneig' : 'Nou Torneig'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Name */}
              <div className="space-y-2">
                <Label>Nom del torneig *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Campionat d'Estiu 2026"
                  data-testid="tournament-name"
                />
              </div>
              
              {/* Date */}
              <div className="space-y-2">
                <Label>Data *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  data-testid="tournament-date"
                />
                {formData.date && (
                  <p className="text-xs text-[#888888]">
                    {isPastDate(formData.date) ? '⚠️ Aquesta data és al passat' : '✓ Torneig futur'}
                  </p>
                )}
              </div>
              
              {/* Image */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  URL de la imatge
                </Label>
                <Input
                  value={formData.image}
                  onChange={(e) => setFormData(prev => ({ ...prev, image: e.target.value }))}
                  placeholder="https://..."
                />
                <p className="text-xs text-[#888888]">
                  Mida recomanada: 800×400 px, format JPG/WebP, màx. 200KB
                </p>
                {formData.image && (
                  <img 
                    src={formData.image} 
                    alt="Preview" 
                    className="mt-2 max-h-32 rounded border object-cover"
                  />
                )}
              </div>
              
              {/* Description */}
              <div className="space-y-2">
                <Label>Descripció</Label>
                <RichTextEditor
                  value={formData.description}
                  onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                  placeholder="Descripció del torneig..."
                  minHeight={120}
                />
              </div>
              
              {/* Links */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <UserCheck className="w-4 h-4" />
                    Enllaç per a Membres
                  </Label>
                  <Input
                    value={formData.link_members}
                    onChange={(e) => setFormData(prev => ({ ...prev, link_members: e.target.value }))}
                    placeholder="https://members.teeone.golf/..."
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Enllaç per a Visitants
                  </Label>
                  <Input
                    value={formData.link_visitors}
                    onChange={(e) => setFormData(prev => ({ ...prev, link_visitors: e.target.value }))}
                    placeholder="https://open.teeone.golf/..."
                  />
                </div>
              </div>
              
              {/* Publish toggle */}
              <div className="flex items-center gap-3 p-4 bg-[#F9F9F7] rounded-lg">
                <input
                  type="checkbox"
                  id="is_published"
                  checked={formData.is_published}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_published: e.target.checked }))}
                  className="w-5 h-5 rounded border-gray-300 text-[#1A4D2E] focus:ring-[#1A4D2E]"
                />
                <div>
                  <Label htmlFor="is_published" className="cursor-pointer font-medium">
                    Publicar torneig
                  </Label>
                  <p className="text-xs text-[#666666]">
                    Si està publicat, serà visible al web públic
                  </p>
                </div>
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={closeEditor}>
                Cancel·lar
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#1A4D2E] hover:bg-[#153d25] text-white gap-2"
                data-testid="save-tournament-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {editingTournament ? 'Actualitzar' : 'Crear'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminTournaments;
